"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import {
  computeWeddingOrder,
  type CalcItem,
  type DesignKey,
  type SizeKey,
} from "@/lib/wedding/calculator";
import { CALC_DESIGN_ORDER } from "@/lib/content/wedding";
import type { WeddingStrings } from "@/lib/i18n/wedding-strings";
import { useLocaleHref } from "@/lib/i18n/use-locale-href";

// PASTE #229/#231 Wave 3: interactive stem-to-box calculator (progressive
// enhancement over the server-rendered intro + reference table).
//
// PRICING GATE: this component computes and renders QUANTITIES ONLY -
// there is no price data in its props or its bundle. The cost panel is
// a CTA for logged-out visitors and a "start your order" path for
// signed-in clients; the actual per-variety wholesale pricing lives on
// the gated catalog, never here. `signedIn` only flips which CTA shows.
//
// All copy comes in via `strings`/`inputLabels` so EN + ES render from
// the same locale-aware source (lib/i18n/wedding-strings.ts).

type CalcStrings = WeddingStrings["calc"];
type InputLabels = WeddingStrings["shared"]["calcInputLabels"];

type Row = { size: SizeKey; qty: number };

export function WeddingCalculator({
  signedIn,
  strings,
  inputLabels,
}: {
  signedIn: boolean;
  strings: CalcStrings;
  inputLabels: InputLabels;
}) {
  const lh = useLocaleHref();
  const sizeOptions: { key: SizeKey; letter: string; full: string }[] = [
    { key: "small", letter: strings.sizes.letters[0], full: strings.sizes.small },
    { key: "medium", letter: strings.sizes.letters[1], full: strings.sizes.medium },
    { key: "large", letter: strings.sizes.letters[2], full: strings.sizes.large },
  ];

  const [rows, setRows] = useState<Record<DesignKey, Row>>(() => {
    const init = {} as Record<DesignKey, Row>;
    for (const d of CALC_DESIGN_ORDER) init[d] = { size: "medium", qty: 0 };
    return init;
  });

  const setQty = (d: DesignKey, next: number) =>
    setRows((r) => ({ ...r, [d]: { ...r[d], qty: Math.max(0, next) } }));
  const setSize = (d: DesignKey, size: SizeKey) =>
    setRows((r) => ({ ...r, [d]: { ...r[d], size } }));

  const items: CalcItem[] = useMemo(
    () =>
      CALC_DESIGN_ORDER.map((d) => ({
        design: d,
        size: rows[d].size,
        qty: rows[d].qty,
      })),
    [rows],
  );

  const order = useMemo(() => computeWeddingOrder(items), [items]);
  const hasAny = items.some((i) => i.qty > 0);
  const u = strings.units;

  return (
    <div className="mt-8 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
      {/* Inputs */}
      <div className="rounded-2xl border border-brand-teal/15 bg-white p-5 shadow-sm md:p-6">
        <h3 className="text-lg font-bold">{strings.designsHeading}</h3>
        <div className="mt-4 divide-y divide-brand-teal/10">
          {CALC_DESIGN_ORDER.map((d) => {
            const row = rows[d];
            const label = inputLabels[d];
            return (
              <div
                key={d}
                className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="font-medium">{label}</span>
                <div className="flex items-center gap-3">
                  {/* Size toggle */}
                  <div
                    className="flex overflow-hidden rounded-full border border-brand-teal/30"
                    role="group"
                    aria-label={`${label} - ${strings.sizes.small}/${strings.sizes.medium}/${strings.sizes.large}`}
                  >
                    {sizeOptions.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setSize(d, s.key)}
                        aria-pressed={row.size === s.key}
                        aria-label={`${s.full} - ${label}`}
                        className={`h-9 w-9 text-sm font-semibold transition-colors ${
                          row.size === s.key
                            ? "bg-brand-teal text-white"
                            : "bg-white text-brand-text/60 hover:bg-brand-teal/5"
                        }`}
                      >
                        {s.letter}
                      </button>
                    ))}
                  </div>
                  {/* Quantity counter */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setQty(d, row.qty - 1)}
                      aria-label={`- ${label}`}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-teal/30 text-lg font-bold text-brand-teal hover:bg-brand-teal/5"
                    >
                      &minus;
                    </button>
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={row.qty}
                      onChange={(e) =>
                        setQty(d, Math.floor(Number(e.target.value) || 0))
                      }
                      aria-label={label}
                      className="h-9 w-14 rounded-lg border border-brand-teal/20 text-center text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setQty(d, row.qty + 1)}
                      aria-label={`+ ${label}`}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-teal/30 text-lg font-bold text-brand-teal hover:bg-brand-teal/5"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Outputs */}
      <div className="rounded-2xl border border-brand-teal/20 bg-brand-teal/5 p-5 shadow-sm md:p-6">
        <h3 className="text-lg font-bold">{strings.estimateHeading}</h3>
        <p className="mt-1 text-sm text-brand-text/60">{strings.estimateNote}</p>

        {!hasAny ? (
          <p className="mt-6 text-sm text-brand-text/60">{strings.emptyPrompt}</p>
        ) : (
          <dl className="mt-6 space-y-4 text-sm">
            <Metric
              label={strings.metric.roses}
              value={`${order.roses.stems} ${u.stems}`}
            >
              {order.roses.bunches} {u.bunches} &middot; {order.roses.quarterBoxes}{" "}
              {order.roses.quarterBoxes === 1 ? u.quarterBox : u.quarterBoxes} ({u.or}{" "}
              {order.roses.halfBoxes}{" "}
              {order.roses.halfBoxes === 1 ? u.halfBox : u.halfBoxes})
            </Metric>
            <Metric
              label={strings.metric.greenery}
              value={`${order.greenery.stems} ${u.stems}`}
            >
              {order.greenery.bunches} {u.bunches}
            </Metric>
            <Metric
              label={strings.metric.hydrangea}
              value={`${order.hydrangea.heads} ${u.heads}`}
            >
              {u.byTheHead}
            </Metric>
          </dl>
        )}

        {/* Pricing gate - CTA only, never a price */}
        <div className="mt-6 border-t border-brand-teal/15 pt-5">
          {signedIn ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-brand-text">
                {strings.gated.signedInMsg}
              </p>
              <Link
                href={lh("/catalog")}
                className="block rounded-full bg-brand-teal px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-brand-teal/90"
              >
                {strings.gated.startOrder} &rarr;
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <Link
                href={lh("/sign-in")}
                className="block rounded-full bg-brand-teal px-6 py-3 text-center font-semibold text-white transition-colors hover:bg-brand-teal/90"
              >
                {strings.gated.signInToSee} &rarr;
              </Link>
              <Link
                href={lh("/sign-up")}
                className="block rounded-full border border-brand-teal px-6 py-3 text-center font-semibold text-brand-teal transition-colors hover:bg-brand-teal/5"
              >
                {strings.gated.applyForAccess} &rarr;
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-brand-text/70">{label}</dt>
      <dd className="text-right">
        <span className="font-bold text-brand-text">{value}</span>
        <span className="block text-xs text-brand-text/55">{children}</span>
      </dd>
    </div>
  );
}
